import React from "react";
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  Stack,
  Divider,
  Chip,
  Link, 
} from "@mui/material";
import { Link as RouterLink } from "react-router-dom";

const Section = ({ title, children }: any) => (
  <Card sx={{ borderRadius: 3, boxShadow: 1 }}>
    <CardContent>
      <Typography variant="h6" fontWeight={600} gutterBottom>
        {title}
      </Typography>
      <Divider sx={{ mb: 2 }} />
      {children}
    </CardContent>
  </Card>
);

const Terms: React.FC = () => {
  return (
    <Container maxWidth="md" sx={{ py: 6 }}>
      {/* Header */}
      <Box sx={{ mb: 5 }}>
        <Typography variant="h4" fontWeight={700} gutterBottom>
          Terms of Service
        </Typography>

        <Stack direction="row" spacing={1}>
          <Chip label="Last updated: April 2026" size="small" />
          <Chip label="Legal" color="primary" size="small" />
        </Stack>
      </Box>

      <Stack spacing={3}>

        {/* 1 */}
        <Section title="1. Overview">
          <Typography paragraph>
            This Service provides AI-powered tools for processing media files,
            including transcription, summarization, format conversion, compression,
            and editing.
          </Typography>
          <Typography paragraph>
            By accessing or using the Service, you agree to be bound by these Terms.
          </Typography>
        </Section>

        {/* 2 */}
        <Section title="2. User Responsibility">
          <Typography paragraph>
            You are solely responsible for any content you upload, process, or
            distribute using the Service.
          </Typography>

          <Typography fontWeight={600}>You agree that you will:</Typography>
          <ul>
            <li>Only upload content you own or have permission to use</li>
            <li>Comply with all applicable laws and regulations</li>
            <li>Respect copyright, privacy, and intellectual property rights</li>
          </ul>

          <Typography fontWeight={600} sx={{ mt: 2 }}>
            You must NOT use this Service to:
          </Typography>
          <ul>
            <li>Process or distribute copyrighted material without authorization</li>
            <li>Upload harmful, illegal, or abusive content</li>
            <li>Attempt to exploit, disrupt, or misuse the platform</li>
          </ul>
        </Section>

        {/* 3 */}
        <Section title="3. User Content & Storage">
          <Typography paragraph>
            Files uploaded to the Service are processed temporarily and are not
            stored permanently unless explicitly required for functionality.
          </Typography>

          <Typography paragraph>
            Processed files may be automatically deleted after download or after a
            limited time period for system efficiency.
          </Typography>

          <Typography>
            You are responsible for downloading and storing your results.
          </Typography>
        </Section>

        {/* 4 */}
        <Section title="4. AI Processing Disclaimer">
          <Typography paragraph>
            The Service uses artificial intelligence to generate outputs such as
            transcripts and summaries.
          </Typography>

          <Typography>We do not guarantee:</Typography>
          <ul>
            <li>Accuracy or completeness of AI-generated content</li>
            <li>Error-free transcription or summarization</li>
            <li>Suitability for legal, medical, or professional use</li>
          </ul>
        </Section>

        {/* 5 */}
        <Section title="5. Service Availability">
          <Typography paragraph>
            We may modify, suspend, or discontinue the Service at any time without notice.
          </Typography>
          <Typography>We do not guarantee:</Typography>
          <ul>
            <li>Continuous or uninterrupted availability</li>
            <li>Error-free operation</li>
            <li>Compatibility with all file types or formats</li>
          </ul>
        </Section>

        {/* 6 */}
        <Section title="6. Payments, Subscriptions & Refunds">
          <ul>
            <li>
              Payments are processed securely through third-party providers (e.g., Stripe). We do not store payment details.
            </li>
            <li>
              Subscriptions renew automatically unless canceled before the billing date.
            </li>
            <li>
              You may cancel at any time. Access continues until the end of the billing cycle.
            </li>
            <li>
              Refunds are not guaranteed but may be issued in cases of technical failure or billing errors within 14 days.
            </li>
            <li>
              By purchasing, you acknowledge that digital services begin immediately and may not be eligible for withdrawal under applicable laws.
            </li>
          </ul>
        </Section>

        {/* 7 */}
        <Section title="7. Limitation of Liability">
          <Typography>To the fullest extent permitted by law, we are not liable for:</Typography>
          <ul>
            <li>Indirect, incidental, or consequential damages</li>
            <li>Loss of data, files, or processed outputs</li>
            <li>Business interruption or financial loss</li>
            <li>Use or misuse of AI-generated content</li>
          </ul>
        </Section>

        {/* 8 */}
        <Section title="8. Termination">
          <Typography>
            We reserve the right to suspend or terminate access to the Service if you violate these Terms or misuse the platform.
          </Typography>
        </Section>

        {/* 9 */}
        <Section title="9. Changes to Terms">
          <Typography>
            We may update these Terms at any time. Continued use of the Service
            constitutes acceptance of the updated Terms.
          </Typography>
        </Section>

        {/* 10 */}
        <Section title="10. Contact">
          <Typography>
            <Link component={RouterLink} to="/contact" sx={{ mx: 1 }}>
                      Contact Support
            </Link>
          </Typography>
        </Section>

      </Stack>
    </Container>
  );
};

export default Terms;